import { Container, Box, Typography, Card, CardContent, TextField, Grid } from '@mui/material';
import DateAdapter from '@mui/lab/AdapterDateFns';
import { DesktopDatePicker, LoadingButton, LocalizationProvider } from '@mui/lab';
import { useState, useEffect } from 'react';
import PaginationTable from 'components/tables/PaginationTable';
import { format } from 'date-fns';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { useBusinessEntityState, useHttpApi } from 'state';
import PDFDownloadIcon from 'assets/images/pdf-icon.png';
import CSVDownloadIcon from 'assets/images/csv-icon.png';
import { useSnackbar } from 'notistack';
import { saveAs } from 'file-saver';

const NAVReports = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { getTokenNavHistory, downloadTokenNavHistoryReport, getTokenNavHistoryReport } =
    useHttpApi();
  const { currentToken } = useBusinessEntityState();
  const [navData, setNavData] = useState([]);
  const [data, setData] = useState([]);
  const [skipPageReset, setSkipPageReset] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [page, setPage] = useState(1);
  const queryParams = `?page=${page}&from=${fromDate || ''}&to=${toDate || ''}`;
  const [totalCount, setTotalCount] = useState(0);
  const [showError, setShowError] = useState(false);
  const [navChartData, setNavChartData] = useState([]);

  const fetchTokenNavHistory = async () => {
    if (!fromDate || !toDate) {
      setShowError(true);
      return;
    }
    const res = await getTokenNavHistoryReport(currentToken?.token_id, queryParams);

    setNavData(res.results);
    setTotalCount(res.count);
  };

  const fetchTokenNavHistoryChartData = async () => {
    if (!fromDate && !toDate) {
      setShowError(true);
      return;
    }
    const res = await getTokenNavHistory(currentToken?.token_id, queryParams);

    setNavChartData(res.reverse());
  };

  const downloadNavHistoryReport = async (docFormat) => {
    if (!fromDate || !toDate) {
      enqueueSnackbar('Please select a date range', { variant: 'error' });
      return;
    }
    const reportResponse = await downloadTokenNavHistoryReport(
      `?token_id=${currentToken?.token_id}&start_date=${fromDate}&end_date=${toDate}&doc_format=${docFormat}`
    );
    const blob = new Blob([reportResponse], {
      type: docFormat === 'pdf' ? 'application/pdf' : 'text/csv'
    });
    saveAs(blob, `Token_Price_History.${docFormat}`);
  };

  // UseEffects

  useEffect(() => {
    if (fromDate && toDate) {
      fetchTokenNavHistory();
      fetchTokenNavHistoryChartData();
    }
  }, [page]);

  const headCells = [
    {
      accessor: 'date',
      Header: 'Date',
      show: true
    },
    {
      accessor: 'NAV',
      Header: 'Token Price ($)',
      show: true,
      width: 200
    }
  ];

  const updateMyData = (rowIndex, columnId, value) => {
    // We also turn on the flag to not reset the page
    setSkipPageReset(true);
    setData((old) =>
      old.map((row, index) => {
        if (index === rowIndex) {
          return {
            ...old[rowIndex],
            [columnId]: value
          };
        }
        return row;
      })
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          className="custom-tooltip"
          style={{ backgroundColor: '#fff', border: '1px solid #ccc', padding: '10px' }}
        >
          <p>Date: {payload[0]?.payload.date}</p>
          <p>Price: {payload[0]?.value}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <Container sx={{ mt: 4 }}>
      <Card>
        <CardContent>
          {/* Filter */}
          <Grid
            container
            spacing={1}
            sx={{ width: '100%', flexDirection: 'row', flexWrap: 'wrap' }}
          >
            <Grid item lg={3} md={4} sm={8} xs={8}>
              <LocalizationProvider dateAdapter={DateAdapter}>
                <DesktopDatePicker
                  label="From Date *"
                  fullWidth
                  size="small"
                  inputFormat="MM-dd-yyyy"
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(format(e, 'yyyy-MM-dd'));
                  }}
                  renderInput={(params) => (
                    <TextField
                      FormHelperTextProps={{ sx: { color: 'red' } }}
                      fullWidth
                      size="small"
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        readOnly: true // Prevent manual typing
                      }}
                      helperText={showError && !fromDate && 'Select a date'}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item lg={3} md={4} sm={8} xs={8}>
              <LocalizationProvider dateAdapter={DateAdapter}>
                <DesktopDatePicker
                  label="To Date *"
                  fullWidth
                  size="small"
                  inputFormat="MM-dd-yyyy"
                  value={toDate}
                  onChange={(e) => {
                    setToDate(format(e, 'yyyy-MM-dd'));
                  }}
                  renderInput={(params) => (
                    <TextField
                      FormHelperTextProps={{ sx: { color: 'red' } }}
                      fullWidth
                      size="small"
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        readOnly: true // Prevent manual typing
                      }}
                      helperText={showError && !toDate && 'Select a date'}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item lg={3} md={4} sm={12} xs={12} container>
              <LoadingButton
                disabled={!fromDate || !toDate}
                variant="gradient"
                loadingPosition="start"
                onClick={() => {
                  fetchTokenNavHistory();
                  fetchTokenNavHistoryChartData();
                }}
              >
                Filter
              </LoadingButton>
              <Box
                sx={{
                  height: '2.5rem',
                  width: '2.5rem',
                  borderRadius: '0.2rem',
                  backgroundColor: '#fff',
                  color: '#000',
                  boxShadow: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  marginX: 1
                }}
                onClick={async () => {
                  await downloadNavHistoryReport('pdf');
                }}
              >
                <Box
                  component="img"
                  sx={{
                    p: 1
                  }}
                  src={PDFDownloadIcon}
                />
              </Box>
              <Box
                sx={{
                  height: '2.5rem',
                  width: '2.5rem',
                  borderRadius: '0.2rem',
                  backgroundColor: '#fff',
                  color: '#000',
                  boxShadow: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
                onClick={async () => {
                  await downloadNavHistoryReport('csv');
                }}
              >
                <Box
                  component="img"
                  sx={{
                    p: 1
                  }}
                  src={CSVDownloadIcon}
                />
              </Box>
            </Grid>
          </Grid>
          {/* Table */}
          <Typography mt={2}>
            <strong>Token:</strong> {currentToken?.token_name} | {currentToken?.token_symbol}
          </Typography>
          <PaginationTable
            handlePageUpdate={setPage}
            tableTitle="Token Price History"
            columns={headCells}
            refreshFunction={fetchTokenNavHistory}
            data={navData}
            setData={setNavData}
            updateMyData={updateMyData}
            skipPageReset={skipPageReset}
            count={totalCount}
          />
        </CardContent>
      </Card>
      {navChartData.length > 0 && (
        <Card sx={{ mt: 4 }}>
          <CardContent>
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Typography>BTL Token Price ($) </Typography>
            </Box>

            <Box
              sx={{
                width: '100%',
                height: 250,
                backgroundColor: '',
                mb: 0,
                mt: 5
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  width={730}
                  height={250}
                  data={navChartData}
                  margin={{ top: 5, left: 0, right: 50, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend verticalAlign="top" align="right" height={36} />
                  <Line type="monotone" dataKey="NAV" stroke="#8884d8" name="Price" />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default NAVReports;
